import { cn } from "@/lib/utils";
import type { ApplicationMetrics } from "@/gen/rbt/cloud/v1alpha1/application/application_pb";

interface MetricsDisplayProps {
  metrics?: ApplicationMetrics;
  className?: string;
}

// Format a latency value for display. Returns "-" when
// there is no traffic.
function formatLatency(ms: number): string {
  if (ms <= 0) return "-";
  if (ms < 1) return `${(ms * 1000).toFixed(0)}μs`;
  if (ms < 1000) return `${ms.toFixed(1)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function MetricsDisplay({ metrics, className }: MetricsDisplayProps) {
  if (!metrics) return null;

  const rps = metrics.requestsPerSecond;
  const p50 = metrics.p50LatencyMs;
  const p95 = metrics.p95LatencyMs;
  const p99 = metrics.p99LatencyMs;

  return (
    <div
      className={cn(
        "flex items-center gap-4 text-sm text-muted-foreground",
        className
      )}
    >
      {/* RPS. */}
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-foreground">
          {rps.toLocaleString(undefined, {
            maximumFractionDigits: 1,
          })}
        </span>
        <span>RPS</span>
      </div>

      <span className="text-muted-foreground/50">|</span>

      {/* Latency percentiles. */}
      <div className="flex items-center gap-3">
        <span>
          p50:{" "}
          <span className="font-medium text-foreground">
            {formatLatency(p50)}
          </span>
        </span>
        <span>
          p95:{" "}
          <span className="font-medium text-foreground">
            {formatLatency(p95)}
          </span>
        </span>
        <span>
          p99:{" "}
          <span className="font-medium text-foreground">
            {formatLatency(p99)}
          </span>
        </span>
      </div>
    </div>
  );
}
